import {
  parsePhoneNumberFromString,
  type CountryCode,
  type PhoneNumber,
} from "libphonenumber-js/max";

import {
  DEFAULT_PHONE_COUNTRY_CODE,
  LEFT_TO_RIGHT_ISOLATE,
  PHONE_LOCAL_FORMAT_CACHE_MAX_ENTRIES,
  POP_DIRECTIONAL_ISOLATE,
} from "../constants";
import { normalizeTextForMatch } from "./string.helpers";

const localPhoneByRawPhone = new Map<string, string>();

export const normalizePhoneNumber = (
  rawPhone: string,
  countryCode: CountryCode = DEFAULT_PHONE_COUNTRY_CODE,
): string | null => {
  const phoneNumber = parsePhoneNumberFromString(rawPhone, countryCode);

  if (!phoneNumber?.isValid()) {
    return null;
  }

  // Return national format (digits only), e.g.
  return phoneNumber.formatNational().replace(/\D/g, "");
};

/** A digits-only value is an international number missing its leading "+", the way phones are stored. */
const parseStoredPhone = (rawPhone: string): PhoneNumber | undefined => {
  const trimmed = rawPhone.trim();

  return parsePhoneNumberFromString(
    /^\d+$/.test(trimmed) ? `+${trimmed}` : trimmed,
  );
};

/**
 * Checks whether the given string is a valid phone number. Mirrors the lenient
 * parsing used by `formatPhoneLocal`: a digits-only value is treated as an
 * international number that is just missing its leading "+", which matches how
 * phone numbers are persisted across the app (e.g. `972684456...`).
 */
export const isPhoneNumber = (rawPhone: string): boolean => {
  const trimmed = rawPhone?.trim();

  if (!trimmed) {
    return false;
  }

  return parseStoredPhone(trimmed)?.isValid() ?? false;
};

/**
 * The form users read and type: national digits for a home-country number ("0522345678"), international for a
 * foreign one. The foreign form is isolated left-to-right, or a Hebrew line would reverse its digit groups.
 * A value that is not a valid phone is returned as stored.
 */
export const toLocalPhoneFormat = (rawPhone: string): string => {
  const phoneNumber = parseStoredPhone(rawPhone);

  if (!phoneNumber?.isValid()) {
    return rawPhone;
  }

  if (phoneNumber.country === DEFAULT_PHONE_COUNTRY_CODE) {
    return phoneNumber.formatNational().replace(/\D/g, "");
  }

  return `${LEFT_TO_RIGHT_ISOLATE}${phoneNumber.formatInternational()}${POP_DIRECTIONAL_ISOLATE}`;
};

/** `toLocalPhoneFormat` parsed once per phone: grids format the same values on every render and search keystroke. */
export const formatPhoneLocal = (
  rawPhone: string | null | undefined,
): string => {
  if (!rawPhone) {
    return "";
  }

  const cachedLocalPhone = localPhoneByRawPhone.get(rawPhone);

  if (cachedLocalPhone !== undefined) {
    return cachedLocalPhone;
  }

  const localPhone = toLocalPhoneFormat(rawPhone);

  // Full means keep what is cached: clearing would make a grid this large re-parse every row per keystroke.
  if (localPhoneByRawPhone.size < PHONE_LOCAL_FORMAT_CACHE_MAX_ENTRIES) {
    localPhoneByRawPhone.set(rawPhone, localPhone);
  }

  return localPhone;
};

/**
 * The digits of a search made only of digits and phone separators ("052-234 5678", "+972 (52)"), else "".
 * Direction marks are dropped first: a foreign phone copied from the display, or from WhatsApp, carries them.
 */
export const getPhoneSearchDigits = (searchValue: string): string => {
  const normalizedSearch = normalizeTextForMatch(searchValue);

  if (!/^[\d\s\-+().]+$/.test(normalizedSearch)) {
    return "";
  }

  return normalizedSearch.replace(/\D/g, "");
};

/**
 * Whether a phone-shaped search hits a stored phone. Compared on digits against both the stored form
 * ("972522345678") and the local one ("0522345678"), so the typed separators need not match the display.
 */
export const isPhoneSearchMatch = ({
  rawPhone,
  searchValue,
}: {
  rawPhone: string | null | undefined;
  searchValue: string;
}): boolean => {
  const searchDigits = getPhoneSearchDigits(searchValue);

  if (!rawPhone || !searchDigits) {
    return false;
  }

  return [rawPhone, formatPhoneLocal(rawPhone)].some(phoneText =>
    phoneText.replace(/\D/g, "").includes(searchDigits),
  );
};

export const callToClickHandler = (phoneNumber?: string): void => {
  const phone = phoneNumber?.trim();
  if (!phone) {
    return;
  }

  window.location.href = `callto:${phone}`;
};
